import * as React from 'react';

import { unwrapResult } from '@reduxjs/toolkit';
import { Notification } from 'farmhub-ui-core';
import { unflatten } from 'flat';
import { useDispatch } from 'react-redux';

import { formatOrderQuery, paginationInit } from 'utils';

interface Props {
  form?: any;
  initField?: string;
  action: any;
  query?: any;
  initValue?: Array<any>;
  deps?: Array<any>;
  isVisible?: boolean;
  formatData?: (e: any) => any;
  onAfterLoad?: (data: any) => void;
  isSpecificApi?: boolean;
  querySpecific?: any;
  noQuery?: boolean;
  noPagination?: boolean;
}

const useFetchData = ({
  form,
  initField,
  action,
  query = {},
  initValue,
  deps = [],
  isVisible = true,
  formatData,
  onAfterLoad,
  isSpecificApi,
  querySpecific,
  noQuery = false,
  noPagination = false,
}: Props) => {
  const dispatch = useDispatch();

  const [data, setData] = React.useState<any[] | any>([]);
  const [pagination, setPagination] = React.useState(paginationInit);
  const [error, setError] = React.useState(undefined);
  const [isLoading, setIsLoading] = React.useState(true);

  const onReFreshList = async () => {
    const orderQuery = formatOrderQuery(query);

    const standardQuery = unflatten({ ...query });
    if (Object.keys(orderQuery).length > 0) {
      standardQuery.order = orderQuery;
    }

    try {
      if (!query && (!isSpecificApi || !querySpecific)) {
        return;
      }

      const requestQuery = isSpecificApi ? querySpecific : standardQuery;

      let actionResult;
      if (noQuery) {
        actionResult = await dispatch(action());
      } else {
        actionResult = await dispatch(
          action({
            ...requestQuery,
          })
        );
      }

      if (noPagination) {
        const { data: dataResult } = unwrapResult(actionResult);

        onAfterLoad && onAfterLoad(dataResult);
        setData(dataResult);
        return;
      }

      const { pagination: paginationResult, data: dataResult } =
        unwrapResult(actionResult);

      let formatList: any[] = [];
      if (formatData) {
        formatList = (dataResult || []).map(item => formatData(item));
      } else {
        formatList = (dataResult || []).map(item => {
          return {
            key: item.id,
            value: item.id,
            ...item,
          };
        });
      }

      setData(formatList);
      if (form && initField && dataResult && dataResult.length > 0) {
        form.setFieldsValue({ [initField]: dataResult[0].id });
      }

      onAfterLoad && onAfterLoad(formatList);

      setPagination(paginationResult);
    } catch (err: any) {
      setError(err);
      if (![422, 403].includes(err?.statusCode ?? err?.code)) {
        Notification('error', 'Tải dữ liệu thất bại', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    let isMounted = true;

    const orderQuery = formatOrderQuery(query);

    const standardQuery = unflatten({ ...query });
    if (Object.keys(orderQuery).length > 0) {
      standardQuery.order = orderQuery;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (!query && (!isSpecificApi || !querySpecific)) {
          return;
        }

        const requestQuery = isSpecificApi ? querySpecific : standardQuery;

        const actionResult = await dispatch(action({ ...requestQuery }));

        if (noPagination) {
          const { data: dataResult } = unwrapResult(actionResult);

          onAfterLoad && onAfterLoad(dataResult);
          setData(dataResult);
          return;
        }

        const { pagination: paginationResult, data: dataResult } =
          unwrapResult(actionResult);

        let formatList: any[] = [];
        if (formatData) {
          formatList = (dataResult || []).map(item => formatData(item));
        } else {
          formatList = (dataResult || []).map(item => {
            return {
              key: item.id,
              value: item.id,
              ...item,
            };
          });
        }

        setData(formatList);
        if (form && initField && dataResult && dataResult.length > 0) {
          form.setFieldsValue({ [initField]: dataResult[0].id });
        }

        onAfterLoad && onAfterLoad(formatList);

        if (isMounted) {
          setPagination(paginationResult);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err);
          if (![422, 403].includes(err?.statusCode ?? err?.code)) {
            Notification('error', 'Tải dữ liệu thất bại', err);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isVisible) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, deps);

  return {
    isLoading,
    error,
    data: initValue ? [...initValue, ...data] : data,
    setData,
    onReFreshList,
    pagination,
  };
};

export default useFetchData;
